import React, { useMemo } from 'react';
import {
  Checkbox,
  Divider,
  FormControl,
  InputLabel,
  ListItemText,
  MenuItem,
  Select,
  SelectChangeEvent,
} from '@mui/material';
import { CommonOption } from '../types/common.types';
import { sortOptionsByLabel } from '../utils/sortOptions';

const SELECT_ALL_VALUE = '__select_all__';

export interface MultiSelectDropdownProps {
  label: string;
  value: string[];
  onChange: (value: string[]) => void;
  options: CommonOption[];
  placeholder?: string;
  disabled?: boolean;
  fullWidth?: boolean;
  showSelectAll?: boolean;
}

const MultiSelectDropdown: React.FC<MultiSelectDropdownProps> = ({
  label,
  value,
  onChange,
  options,
  placeholder = 'Select',
  disabled = false,
  fullWidth = true,
  showSelectAll = false,
}) => {
  const labelId = `${label.replace(/\s+/g, '-').toLowerCase()}-label`;
  const sortedOptions = useMemo(() => sortOptionsByLabel(options), [options]);
  const selectableValues = options.filter((o) => !o.disabled).map((o) => o.value);
  const allSelected = selectableValues.length > 0 && selectableValues.every((v) => value.indexOf(v) > -1);

  const handleChange = (event: SelectChangeEvent<string[]>) => {
    const val = event.target.value;
    const nextValues = typeof val === 'string' ? val.split(',') : val;

    if (showSelectAll && nextValues.indexOf(SELECT_ALL_VALUE) > -1) {
      onChange(allSelected ? [] : selectableValues);
      return;
    }

    onChange(nextValues);
  };

  return (
    <FormControl fullWidth={fullWidth} size="small" disabled={disabled}>
      <InputLabel id={labelId} shrink>
        {label}
      </InputLabel>
      <Select
        labelId={labelId}
        label={label}
        multiple
        value={value}
        onChange={handleChange}
        displayEmpty
        renderValue={(selected) => {
          if (!selected || selected.length === 0) {
            return <span style={{ color: '#5f6f68' }}>{placeholder}</span>;
          }
          return selected
            .map((val) => options.find((o) => o.value === val)?.label ?? val)
            .join(', ');
        }}
        MenuProps={{
          PaperProps: {
            sx: {
              maxHeight: 300,
              '& .MuiMenuItem-root': {
                minHeight: 38,
                py: 0.5,
              },
            },
          },
        }}
      >
        {showSelectAll && selectableValues.length > 0 && [
          <MenuItem key={SELECT_ALL_VALUE} value={SELECT_ALL_VALUE}>
            <Checkbox
              checked={allSelected}
              indeterminate={!allSelected && value.length > 0}
              size="small"
            />
            <ListItemText primary="Select All" />
          </MenuItem>,
          <Divider key={`${SELECT_ALL_VALUE}-divider`} />,
        ]}
        {sortedOptions.map((option) => (
          <MenuItem key={option.value} value={option.value} disabled={option.disabled}>
            <Checkbox checked={value.indexOf(option.value) > -1} size="small" />
            <ListItemText primary={option.label} />
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default MultiSelectDropdown;
